import isNaN from "lodash-es/isNaN.js";
import isUndefined from "lodash-es/isUndefined.js";
import parseInt from "lodash-es/parseInt.js";
import isEmpty from "lodash-es/isEmpty.js";
import trim from "lodash-es/trim.js";
import regexpEscape from "escape-string-regexp";
import config from "./default-config.js";
export default class Parser {
    filterConfig;
    helpers = {
        parseInt(value) {
            if (!value)
                return undefined;
            const num = parseInt(value);
            if (isNaN(num))
                return undefined;
            return num;
        },
        parseNum(value) {
            if (!value)
                return undefined;
            const num = Number(value);
            if (isNaN(num))
                return undefined;
            return num;
        },
    };
    constructor(filterConfig = config) {
        this.filterConfig = filterConfig;
    }
    getVars(pathSegments, query = {}) {
        let segments = undefined;
        if (Array.isArray(pathSegments)) {
            segments = pathSegments;
        }
        else if (pathSegments && !isEmpty(pathSegments)) {
            segments = [pathSegments];
        }
        return {
            search: this.getSearchTerm(query.search),
            filter: this.getFilterVars(segments, query),
            sort: this.getSortVars(query.sort),
            pagination: this.getPaginationVars(segments, query),
        };
    }
    // Helpers
    getCategoryPathSegments(pathSegments) {
        if (!pathSegments)
            return [];
        const returnValue = [];
        for (let i = 0; i < pathSegments.length; i++) {
            if (pathSegments[i].match(this.filterPattern()) ||
                pathSegments[i].match(this.pagePattern())) {
                return returnValue;
            }
            returnValue.push(pathSegments[i]);
        }
        return returnValue;
    }
    pagePattern() {
        return new RegExp(`^${regexpEscape(this.filterConfig.prefixes.page)}(.+)$`);
    }
    filterPattern() {
        return new RegExp(`^${regexpEscape(this.filterConfig.prefixes.filter)}(.+)$`);
    }
    // Search
    getSearchTerm(search) {
        const term = trim(search || "");
        if (!term)
            return undefined;
        return term;
    }
    // Filters
    getFilterVars(pathSegments, query = {}) {
        return {
            ...this.parseFilter(pathSegments),
            ...this.parseQueryFilter(query),
            onetimePrice: this.getOnetimePriceFilter(query?.minPrice, query?.maxPrice),
            productCategoryPaths: this.getProductCategoryPaths(pathSegments),
        };
    }
    parseFilter(params) {
        const filters = {};
        if (!this.filterConfig.filters)
            return filters;
        this.filterConfig.filters.forEach((filter) => {
            const filterValues = this.getFilterValues(params, filter);
            if (!filterValues)
                return;
            filters[filter.name] = filterValues;
        });
        return filters;
    }
    parseQueryFilter(query = {}) {
        const parameters = this.filterConfig.queryParameters || [];
        const parsedFilters = {};
        parameters.forEach((parameter) => {
            const value = query[parameter.name];
            if (value === undefined)
                return;
            if (parameter.pattern && !parameter.pattern.test(value))
                return;
            if (parameter.parser) {
                const result = parameter.parser(value, query);
                if (result === undefined)
                    return;
                parsedFilters[parameter.name] = result;
                return;
            }
            parsedFilters[parameter.name] = value;
        });
        return parsedFilters;
    }
    getOnetimePriceFilter(minPrice, maxPrice) {
        const min = this.helpers.parseNum(minPrice);
        const max = this.helpers.parseNum(maxPrice);
        const filter = {};
        if (min)
            filter.min = min;
        if (max)
            filter.max = max;
        return filter;
    }
    getProductCategoryPaths(pathSegments) {
        const segments = this.getCategoryPathSegments(pathSegments);
        if (!segments)
            return [];
        let paths;
        const categoryPathSegments = segments.map((segment) => segment.split(this.filterConfig.separators.item).sort());
        const parentPathSegments = [];
        for (let i = 0; i < categoryPathSegments.length; i += 1) {
            const isLeafLevel = i === categoryPathSegments.length - 1;
            const items = categoryPathSegments[i];
            if (isLeafLevel) {
                // When reaching the leaf level, we can put the entire category path
                // together.
                paths = items.map((leafPathSegment) => parentPathSegments
                    .concat([leafPathSegment])
                    .join(this.filterConfig.separators.path));
            }
            else {
                if (items.length > 1) {
                    // I don't know whether it is completely valid to throw here. Another
                    // variant would be to just silently select the first item without
                    // throwing. But be aware that not throwing may lead to multiple list
                    // pages displaying the exactly same content which is not desirable
                    // for SEO.
                    throw new Error("Unable to filter by multiple categories on non-leaf level");
                }
                parentPathSegments.push(items[0]);
            }
        }
        return paths;
    }
    // Pagination
    getPaginationVars(pathSegments, query = {}) {
        return {
            page: this.getPage(pathSegments),
            size: this.getPageSize(query?.pageSize),
        };
    }
    getPage(pathSegments) {
        return this.helpers.parseInt(this.getPagePathSegment(pathSegments)) || 1;
    }
    getPageSize(pageSize) {
        return this.helpers.parseInt(pageSize) || this.filterConfig.defaultPageSize;
    }
    // Sort
    getSortVars(sort) {
        return sort ? this.filterConfig.sortMap[sort] : undefined;
    }
    getFilterValue(params, filter) {
        const filterSegment = this.getFilterPathSegment(params);
        if (!filterSegment)
            return undefined;
        const filterIndex = this.filterConfig.filters.findIndex((f) => f.name === filter.name);
        const value = filterSegment.split(this.filterConfig.separators.filter)[filterIndex];
        if (!value)
            return undefined;
        let placeholder = filter.placeholder;
        const prefix = filter.prefix;
        if (prefix) {
            placeholder = `${prefix}${placeholder}`;
        }
        if (value === placeholder)
            return undefined;
        return value;
    }
    getFilterValues(params, filter) {
        let value = this.getFilterValue(params, filter);
        if (!value)
            return [];
        if (filter.prefix) {
            const regExp = new RegExp(`^${regexpEscape(filter.prefix)}(.+)$`);
            if (!regExp.test(value))
                return [];
            value = value.replace(regExp, "$1");
        }
        const parseFunc = filter.parse ? filter.parse : (v) => v;
        if (filter.multiple) {
            return value
                .split(this.filterConfig.separators.item)
                .map(parseFunc)
                .filter((s) => !isUndefined(s))
                .sort();
        }
        return parseFunc(value) || "";
    }
    getFilterPathSegment(pathSegments) {
        return this.getPathSegmentWithPattern(pathSegments, this.filterPattern());
    }
    getPagePathSegment(pathSegments) {
        return this.getPathSegmentWithPattern(pathSegments, this.pagePattern());
    }
    getPathSegmentWithPattern(pathSegments, pattern) {
        if (!pathSegments)
            return undefined;
        let matchingSegment;
        for (let i = 0; i < pathSegments.length; i += 1) {
            const segment = pathSegments[i];
            const match = segment.match(pattern);
            if (match && match[1]) {
                [, matchingSegment] = match;
                break;
            }
        }
        return matchingSegment;
    }
}
